import { FormattedMessage } from 'react-intl'
import { Link as RouterLink } from '@tanstack/react-router'

import { Link } from '@gto/ui/link'
import { Typography } from '@gto/ui/typography'

import { RbWizardLogo } from '@/assets/icons'
import { type GaTag, useSupportLinks } from '@/components/support-links'
import type { Translations } from '@/localise'

import { AbsoluteSuitsOnCorners } from './absolute-suits-on-corners'
import type { FooterLinkExternal, FooterLinkInternal, FooterLinkProps } from './footer-link'
import { FooterLink } from './footer-link'

export const Footer = () => {
  const supportLinks = useSupportLinks()
  const footerMap = getFooterMap(supportLinks)

  return (
    <footer className="relative flex flex-col gap-16 bg-background-default pb-4 pt-18 before:absolute before:inset-y-0 before:right-full before:z-0 before:w-screen before:bg-[inherit] before:content-[''] after:absolute after:inset-y-0 after:left-full after:z-0 after:w-screen after:bg-[inherit] after:content-[''] sm:pt-12">
      <AbsoluteSuitsOnCorners />
      <div className="flex items-start justify-between gap-4 sm:flex-col sm:items-center sm:gap-12">
        <Link
          aria-label="Go to Rakeback Wizard Home Page"
          as={RouterLink}
          className="flex"
          to="/"
        >
          <RbWizardLogo
            height={32}
            width={200}
          />
        </Link>
        <div className="flex gap-[120px] text-left md:gap-16 sm:flex-col sm:gap-10 sm:text-center">
          {footerMap.map((section, index) => (
            <div
              // eslint-disable-next-line @eslint-react/no-array-index-key -- fixed footer column sections, never reorders
              key={index}
              className="flex flex-col gap-3 sm:gap-1"
            >
              <Typography
                color="text.primary"
                variant="subtitle3"
              >
                <FormattedMessage id={section.title} />
              </Typography>
              {section.links ? (
                section.links.map((link, linkIndex) => {
                  const { tLabel, label, ...rest } = link

                  return (
                    <FooterLink
                      // eslint-disable-next-line @eslint-react/no-array-index-key -- fixed footer link list, never reorders
                      key={linkIndex}
                      {...(rest as Omit<FooterLinkProps, 'children'>)}
                    >
                      {tLabel ? <FormattedMessage id={tLabel} /> : label}
                    </FooterLink>
                  )
                })
              ) : section.component ? (
                <div className="flex gap-2">
                  <section.component />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      <Typography
        className="text-center"
        color="text.secondary"
        variant="body3"
      >
        <FormattedMessage
          id="footer.copyright"
          values={{ year: new Date().getFullYear() }}
        />
      </Typography>
    </footer>
  )
}

type FooterMapRow =
  | (Omit<FooterLinkInternal, 'children'> & {
      tLabel?: keyof Translations
      label?: string
    })
  | (Omit<FooterLinkExternal, 'children'> & {
      tLabel?: keyof Translations
      label?: string
      gaTag?: GaTag
      component?: unknown
    })

type FooterMap = {
  title: keyof Translations
  links?: FooterMapRow[]
  component?: React.ComponentType
}
const getFooterMap = (supportLinks: ReturnType<typeof useSupportLinks>): FooterMap[] => [
  {
    title: 'common.labels.legal',
    links: [
      {
        tLabel: 'common.controls.termsOfService',
        to: '/terms-of-service' as const,
        viewTransition: true,
      },
      {
        tLabel: 'common.controls.privacyPolicy',
        to: '/privacy-policy' as const,
        viewTransition: true,
      },
    ],
  },
  {
    title: 'common.labels.rakeback',
    links: [
      { tLabel: 'common.controls.faq', to: '/faq' as const, viewTransition: true },
      { tLabel: 'common.controls.aboutUs', to: '/about-us' as const, viewTransition: true },
    ],
  },
  {
    title: 'common.labels.support',
    links: supportLinks,
  },
]
